import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Crown, MapPin, Search, ArrowUpRight } from "lucide-react";
import { listingsApi, type OwnerListing } from "@/lib/listings-api";
import { useAppTranslation } from "@/i18n/useAppTranslation";

type State =
  | { status: "loading"; token: string }
  | { status: "error"; token: string }
  | { status: "ready"; token: string; listings: OwnerListing[] };

export function pickPreviewListing(listings: OwnerListing[]): OwnerListing | null {
  const hasPhoto = (listing: OwnerListing) => !!listing.primaryImageUrl;
  return listings.find((listing) => listing.listingStatus === "active" && hasPhoto(listing))
    ?? listings.find(hasPhoto)
    ?? listings.find((listing) => listing.listingStatus === "active")
    ?? listings[0]
    ?? null;
}

export function OwnListingPremierPreview({ token, compact = false, membershipTier }: { token: string; compact?: boolean; membershipTier?: "classic" | "premier" }) {
  const { t } = useAppTranslation();
  const isPremierMember = membershipTier === "premier";
  const [state, setState] = useState<State>({ status: "loading", token });
  const [retry, setRetry] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let settled = false;
    setState({ status: "loading", token });
    setImgFailed(false);
    setSelectedId(null);
    const timeout = setTimeout(() => {
      if (!cancelled && !settled) {
        settled = true;
        setState({ status: "error", token });
      }
    }, 8000);
    listingsApi.getMyListings(token)
      .then((r) => {
        if (!Array.isArray(r.listings)) throw new Error(t("professional.proOwner.own_preview_error"));
        if (!cancelled && !settled) {
          settled = true;
          clearTimeout(timeout);
          setState({ status: "ready", token, listings: r.listings });
        }
      })
      .catch(() => {
        if (!cancelled && !settled) {
          settled = true;
          clearTimeout(timeout);
          setState({ status: "error", token });
        }
      });
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [token, retry, t]);

  const shell = (children: React.ReactNode) => (
    <section className={`rounded-3xl bg-brand/5 ${compact ? "p-4 sm:p-5" : "p-5 sm:p-8"}`} aria-labelledby="own-premier-preview-title" data-testid="own-premier-preview">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand">{isPremierMember ? t("subscription.benefits.title", "Your Benefits") : t("professional.proOwner.own_preview_eyebrow", "Premier preview")}</p>
      <h3 id="own-premier-preview-title" className="mt-2 font-serif text-2xl text-brand sm:text-3xl">
        {isPremierMember
          ? t("professional.proOwner.own_member_title", "Your listing with Premier benefits")
          : membershipTier === "classic"
            ? t("professional.proOwner.own_upgrade_title", "Preview your Premier upgrade")
            : t("professional.proOwner.own_preview_title", "How your listing could look with a Premier badge")}
      </h3>
      {children}
    </section>
  );

  const visibleState = state.token === token ? state : { status: "loading" as const, token };
  if (visibleState.status === "loading") {
    return shell(
      <div role="status" aria-busy="true" aria-label={t("common.loading")} data-testid="own-premier-preview-loading">
        <div aria-hidden="true" className={`mt-4 grid items-center gap-5 ${compact ? "" : "lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]"}`}>
          <div className="overflow-hidden rounded-2xl bg-white shadow-xl shadow-brand/15">
            <div className="bac-shimmer aspect-[4/3]" />
            <div className="space-y-3 p-5">
              <div className="bac-shimmer h-5 w-3/4 rounded" />
              <div className="bac-shimmer h-3 w-1/2 rounded" />
              <div className="bac-shimmer h-8 w-24 rounded-full" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="bac-shimmer h-6 w-3/4 rounded" />
            <div className="bac-shimmer h-4 w-full rounded" />
            <div className="bac-shimmer h-4 w-5/6 rounded" />
          </div>
        </div>
      </div>,
    );
  }
  if (visibleState.status === "error") {
    return shell(
      <div className="mt-5 flex flex-col gap-3 text-sm text-foreground/80 sm:flex-row sm:items-center sm:justify-between" role="alert" data-testid="own-premier-preview-error">
        <span>{t("professional.proOwner.own_preview_error", "We couldn't load your listings, so no preview is available right now.")}</span>
        <button type="button" onClick={() => setRetry((k) => k + 1)} className="min-h-11 rounded-lg border border-brand px-4 font-semibold text-brand hover:bg-brand/5">
          {t("common.retry", "Try again")}
        </button>
      </div>,
    );
  }
  const car = visibleState.listings.find((listing) => listing.id === selectedId) ?? pickPreviewListing(visibleState.listings);
  if (!car) {
    return shell(
      <div className="mt-5 text-sm text-foreground/80" data-testid="own-premier-preview-empty">
        <p>{t("professional.proOwner.own_preview_empty", "You don't have a listing yet. Once you list a car, you'll see a preview of it here.")}</p>
        <Link href="/dashboard/listings" className="mt-3 inline-flex min-h-11 items-center gap-1 font-semibold text-brand underline underline-offset-4">
          {t("professional.proOwner.own_preview_garage", "Go to My Garage")} <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>,
    );
  }
  const name = [car.year, car.make, car.model].filter(Boolean).join(" ");
  const place = [car.city, car.state].filter(Boolean).join(", ");
  return shell(
    <>
    {visibleState.listings.length > 1 && (
      <label className="mt-5 flex flex-col gap-2 text-sm font-medium text-brand">
        {t("professional.proOwner.own_preview_choose", "Choose a listing to preview")}
        <select
          value={car.id}
          onChange={(event) => { setSelectedId(Number(event.target.value)); setImgFailed(false); }}
          className="min-h-11 w-full rounded-xl border border-brand/20 bg-white px-3 py-2 sm:max-w-sm"
          data-testid="select-own-preview-listing"
        >
          {visibleState.listings.map((listing) => (
            <option key={listing.id} value={listing.id}>{[listing.year, listing.make, listing.model].filter(Boolean).join(" ")}</option>
          ))}
        </select>
      </label>
    )}
    <div className={`mt-4 grid items-center gap-5 ${compact ? "" : "lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]"}`}>
      <div className="overflow-hidden rounded-2xl bg-white shadow-xl shadow-brand/15">
        <div className="relative aspect-[4/3] bg-brand/10">
          {car.primaryImageUrl && !imgFailed ? (
            <img src={car.primaryImageUrl} alt={name} loading="lazy" decoding="async" onError={() => setImgFailed(true)} className="h-full w-full object-cover" data-testid="img-own-preview-car" />
          ) : (
            <div className="flex h-full items-center justify-center bg-brand px-4 text-center text-sm font-medium text-white">{t("professional.proOwner.own_preview_no_photo", "Listing photo unavailable")}</div>
          )}
          <div className={`absolute left-3 right-3 top-3 flex flex-wrap items-start gap-2 ${isPremierMember ? "justify-end" : "justify-between"}`}>
            {!isPremierMember && <span className="rounded-full bg-brand px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white shadow-md">{t("professional.proOwner.own_preview_only", "Preview only")}</span>}
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-brand shadow-md" data-testid="badge-own-preview-premier">
              <Crown className="h-3 w-3" /> {t("carCard.pro_owner", "Pro Owner")}
            </span>
          </div>
        </div>
        <div className={compact ? "p-4" : "p-5"}>
          <h4 className="font-serif text-xl text-brand" data-testid="text-own-preview-title">{name}</h4>
          {place && <p className="mt-1 flex items-center gap-1.5 text-sm text-foreground/70" data-testid="text-own-preview-location"><MapPin className="h-4 w-4" />{place}</p>}
        </div>
      </div>
      <div className="space-y-4 border-l-2 border-amber-500 pl-5 text-sm leading-relaxed text-foreground/80">
        <p className="flex items-start gap-2"><Crown className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{t("professional.proOwner.own_preview_badge", "Premier adds a Pro Owner badge to your listing card.")}</p>
        <p className="flex items-start gap-2"><Search className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{t("professional.proOwner.own_preview_boost", "Premier also gives your listing a search ranking boost.")}</p>
        <p className="text-xs text-foreground/70" data-testid="text-own-preview-disclaimer">
          {isPremierMember
            ? t("professional.proOwner.own_member_disclaimer", "Your Premier benefits are active. This card uses your listing's real photo, name and location.")
            : membershipTier === "classic"
              ? t("professional.proOwner.own_upgrade_disclaimer", "Your Classic membership is active. This preview shows the additional Premier badge and does not change your live listing.")
              : t("professional.proOwner.own_preview_disclaimer", "This is a Premier preview using your listing's real photo, name and location. It is not an active membership, and it does not change your live listing.")}
        </p>
      </div>
    </div>
    </>,
  );
}
